import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowUpRight, ArrowDownLeft, Plus, Banknote, Coins, Eye, EyeOff, Copy, Split, Send as SendIcon, PiggyBank, ArrowRightLeft, Repeat, BarChart3, Gift, Landmark, HandCoins, ChevronRight } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { FullLoader } from "../components/common";

const PRIMARY = [
  { id: "send", label: "Send", icon: ArrowUpRight, to: "/app/send" },
  { id: "request", label: "Request", icon: HandCoins, to: "/app/request" },
  { id: "add", label: "Add Money", icon: Plus, to: "/app/add-money" },
  { id: "withdraw", label: "Withdraw", icon: Banknote, to: "/app/withdraw" },
];

const TOOLS = [
  { id: "savings", label: "Savings", icon: PiggyBank, to: "/app/savings" },
  { id: "convert", label: "Convert", icon: ArrowRightLeft, to: "/app/converter" },
  { id: "crypto", label: "Crypto", icon: Coins, to: "/app/crypto" },
];

function SectionLabel({ children }) {
  return <h2 className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground mt-7 mb-3 px-0.5">{children}</h2>;
}

export default function Dashboard() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [wallet, setWallet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hide, setHide] = useState(false);
  const [pendingReq, setPendingReq] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const w = await api.get("/wallet");
        setWallet(w.data);
      } catch (e) { toast.error(errMsg(e)); }
      finally { setLoading(false); }
    })();
    api.get("/requests")
      .then(({ data }) => setPendingReq((data || []).filter((r) => r.direction === "incoming" && r.status === "pending").length))
      .catch(() => {});
  }, []);

  const copyTag = () => { navigator.clipboard.writeText(user.cash_tag); toast.success("Cash Tag copied"); };

  if (loading || !wallet) return <FullLoader />;

  const isTeen = user.family?.is_teen;
  const primary = PRIMARY.filter((a) => !(isTeen && ["add", "withdraw"].includes(a.id)));
  const tools = TOOLS.filter((a) => !(isTeen && a.id === "crypto"));
  const hr = new Date().getHours();
  const greeting = hr < 12 ? "Good morning" : hr < 18 ? "Good afternoon" : "Good evening";

  return (
    <div className="animate-fade-up">
      <p className="text-muted-foreground">{greeting},</p>
      <h1 className="font-display text-3xl font-semibold tracking-tight mb-5">
        {(user.display_name || "").trim().split(/\s+/)[0]}{" "}
        <span className="wave-hand inline-block">👋</span>
      </h1>

      {/* Balance card */}
      <div className="relative overflow-hidden rounded-3xl p-6 text-white shadow-[0_18px_44px_rgba(75,35,204,0.4)]"
        style={{ background: "linear-gradient(135deg,#6B3BF5 0%,#4B23CC 55%,#3A1A9F 100%)" }}>
        <div className="absolute -right-10 -top-10 w-40 h-40 rounded-full bg-white/10" />
        <div className="absolute -right-4 top-16 w-28 h-28 rounded-full bg-white/5" />
        <div className="relative">
          <div className="flex items-center justify-between">
            <span className="text-xs tracking-[0.18em] uppercase font-semibold text-white/70">Available Balance</span>
            <button data-testid="toggle-balance-btn" onClick={() => setHide(!hide)} className="text-white/80">{hide ? <EyeOff size={18} /> : <Eye size={18} />}</button>
          </div>
          <div data-testid="balance-amount" className="amount-figure text-4xl sm:text-5xl mt-2 tracking-tight tabular-nums leading-none">
            {hide ? <span className="tracking-[0.12em]">$ ••••••••</span> : wallet.available_display}
          </div>
          <button data-testid="dashboard-cashtag" onClick={copyTag}
            className="mt-5 inline-flex items-center gap-2 bg-white/15 rounded-full px-3 py-1.5 text-sm font-mono transition-transform duration-200 active:scale-95">
            {user.cash_tag} <Copy size={14} />
          </button>
        </div>
      </div>

      {/* Primary actions */}
      <div className="grid grid-cols-4 gap-2 mt-6">
        {primary.map(({ id, label, icon: Icon, to }) => (
          <button key={id} data-testid={`action-${id}`} onClick={() => nav(to)}
            className="flex flex-col items-center gap-2 py-3 rounded-2xl transition-transform duration-200 active:scale-95">
            <span className="grid place-items-center w-12 h-12 rounded-2xl bg-secondary text-primary">
              <Icon size={21} />
            </span>
            <span className="text-[11px] font-medium text-center leading-tight">{label}</span>
          </button>
        ))}
      </div>

      {/* Money tools */}
      <SectionLabel>Money tools</SectionLabel>
      <div className="grid grid-cols-3 gap-3">
        {tools.map(({ id, label, icon: Icon, to }) => (
          <button key={id} data-testid={`action-${id}`} onClick={() => nav(to)}
            className="flex flex-col items-center justify-center gap-2 py-4 rounded-2xl bg-card border border-border transition-transform duration-200 active:scale-[0.97]">
            <span className="grid place-items-center w-10 h-10 rounded-xl bg-secondary text-primary"><Icon size={18} /></span>
            <span className="text-xs font-semibold">{label}</span>
          </button>
        ))}
      </div>

      {/* Jack Send */}
      {!isTeen && (
        <>
          <SectionLabel>Jack Send</SectionLabel>
          <button data-testid="action-jacksend" onClick={() => nav("/app/jacksend")}
            className="w-full flex items-center gap-3 p-4 rounded-2xl bg-card border border-border transition-transform duration-200 active:scale-[0.98]">
            <span className="grid place-items-center w-10 h-10 rounded-xl bg-primary/10 text-primary shrink-0"><SendIcon size={18} /></span>
            <span className="flex-1 text-left">
              <span className="block text-sm font-semibold">Jack Send</span>
              <span className="block text-[11px] text-muted-foreground">Send money instantly using a phone number or email</span>
            </span>
            <ChevronRight size={18} className="text-muted-foreground" />
          </button>
        </>
      )}

      {/* Bills & payments */}
      <SectionLabel>Bills & payments</SectionLabel>
      <div className="space-y-3">
        <button data-testid="action-split" onClick={() => nav("/app/split")}
          className="w-full flex items-center gap-3 p-4 rounded-2xl bg-card border border-border transition-transform duration-200 active:scale-[0.98]">
          <span className="grid place-items-center w-10 h-10 rounded-xl bg-secondary text-primary shrink-0"><Split size={18} /></span>
          <span className="flex-1 text-left">
            <span className="block text-sm font-semibold">Split a bill</span>
            <span className="block text-[11px] text-muted-foreground">Share a cost evenly with friends</span>
          </span>
          <ChevronRight size={18} className="text-muted-foreground" />
        </button>
        <button data-testid="action-recurring" onClick={() => nav("/app/recurring")}
          className="w-full flex items-center gap-3 p-4 rounded-2xl bg-card border border-border transition-transform duration-200 active:scale-[0.98]">
          <span className="grid place-items-center w-10 h-10 rounded-xl bg-primary/10 text-primary shrink-0"><Repeat size={18} /></span>
          <span className="flex-1 text-left">
            <span className="block text-sm font-semibold">Recurring payments</span>
            <span className="block text-[11px] text-muted-foreground">Automate payments on a schedule</span>
          </span>
          <ChevronRight size={18} className="text-muted-foreground" />
        </button>
      </div>

      {/* Requests */}
      <SectionLabel>Requests</SectionLabel>
      <button data-testid="action-requests" onClick={() => nav("/app/requests")}
        className="w-full flex items-center gap-3 p-4 rounded-2xl bg-card border border-border transition-transform duration-200 active:scale-[0.98]">
        <span className="grid place-items-center w-10 h-10 rounded-xl bg-secondary text-primary shrink-0"><ArrowDownLeft size={18} /></span>
        <span className="flex-1 text-left">
          <span className="block text-sm font-semibold">Requests</span>
          <span className="block text-[11px] text-muted-foreground">
            {pendingReq > 0 ? `${pendingReq} request${pendingReq > 1 ? "s" : ""} waiting for you` : "Review money people asked you for"}
          </span>
        </span>
        {pendingReq > 0 && (
          <span data-testid="requests-pending-count" className="grid place-items-center min-w-6 h-6 px-1.5 rounded-full bg-primary text-primary-foreground text-xs font-semibold">{pendingReq}</span>
        )}
        <ChevronRight size={18} className="text-muted-foreground" />
      </button>

      {/* Insights */}
      <SectionLabel>Insights</SectionLabel>
      <button data-testid="action-insights" onClick={() => nav("/app/insights")}
        className="w-full flex items-center gap-3 p-4 rounded-2xl bg-gradient-to-r from-primary/10 to-primary/5 border border-primary/15 transition-transform duration-200 active:scale-[0.98]">
        <span className="grid place-items-center w-10 h-10 rounded-xl bg-primary text-primary-foreground shrink-0"><BarChart3 size={18} /></span>
        <span className="flex-1 text-left">
          <span className="block text-sm font-semibold">Spending insights</span>
          <span className="block text-[11px] text-muted-foreground">See where your money goes</span>
        </span>
        <ChevronRight size={18} className="text-primary" />
      </button>

      {/* Promo */}
      <SectionLabel>More</SectionLabel>
      <button data-testid="action-referral" onClick={() => nav("/app/referral")}
        className="w-full flex items-center gap-3 p-4 rounded-2xl bg-gradient-to-r from-success/10 to-success/5 border border-success/20 transition-transform duration-200 active:scale-[0.98]">
        <span className="grid place-items-center w-10 h-10 rounded-xl bg-success text-white shrink-0"><Gift size={18} /></span>
        <span className="flex-1 text-left">
          <span className="block text-sm font-semibold">Invite friends, earn $5</span>
          <span className="block text-[11px] text-muted-foreground">You both get rewarded after their first deposit</span>
        </span>
        <ChevronRight size={18} className="text-success" />
      </button>

      {/* Business banking */}
      {!isTeen && (
        <button data-testid="action-banking" onClick={() => nav("/app/banking")}
          className="mt-3 w-full flex items-center gap-3 p-4 rounded-2xl bg-card border border-border transition-transform duration-200 active:scale-[0.98]">
          <span className="grid place-items-center w-10 h-10 rounded-xl bg-primary/10 text-primary shrink-0"><Landmark size={18} /></span>
          <span className="flex-1 text-left">
            <span className="block text-sm font-semibold">Business banking</span>
            <span className="block text-[11px] text-muted-foreground">Account & routing number via Stripe Treasury</span>
          </span>
          <ChevronRight size={18} className="text-muted-foreground" />
        </button>
      )}
    </div>
  );
}
